import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Drawer, Dropdown } from 'antd';
import {
  MailOutlined,
  MenuOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import qrFrame from '../assets/images/qr-frame.png';
import qrPlaceholder from '../assets/images/product-accounting-illustration.png';
import logo from '../assets/images/logo.png';
import logo_white from '../assets/images/logo-white.png';
import ChatWidget from './ChatWidget';

const { Header, Content, Footer } = Layout;

// 产品服务下的子章节(用于悬浮下拉框)
const productSubItems = [
  { key: 'advantage', label: '产品优势' },
  { key: 'architecture', label: '产品架构' },
  { key: 'capability', label: '能力图谱' },
  { key: 'core-capability', label: '核心产品能力' },
];

const menuItems = [
  { key: '/', label: '首页' },
  { key: '/products', label: '产品服务' },
  { key: '/solutions', label: '解决方案' },
  { key: '/news', label: '资讯' },
  { key: '/about', label: '关于我们' },
  { key: '/contact', label: '联系我们' },
];

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // 登录 / 体验演示 —— 直接在当前页跳转,不开新标签页
  const openErpInSameTab = (url) => {
    window.location.href = url;
  };
  // 顶部右侧按钮文案态 —— true 时显示「留言」(原 /contact 行为),
  // false 时显示「登录」(点击后跳 ERP 登录页 + 切回「留言」)
  const [showAsMessage, setShowAsMessage] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // 路由变化时滚到顶部 ——
  // SPA 默认行为是「保留旧页滚动位置」,但产品页内的 hash 跳转需要单独处理
  // (比如点击产品服务下拉里的「产品优势」,要滚到对应章节而不是顶部)
  useEffect(() => {
    // 带 hash 的由各页面自己的 useEffect 处理(Product 页面已实现),这里直接滚顶
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location.pathname, location.hash]);

  const currentKey =
    location.pathname === '/' ? '/' : `/${location.pathname.split('/')[1]}`;

  const handleMenuClick = ({ key }) => {
    navigate(key);
    setDrawerOpen(false);
  };

  // 点击下拉框中某个子章节:如果已经在产品页则只滚动,否则先跳转
  const handleSubItemClick = ({ key }) => {
    if (location.pathname === '/products') {
      const el = document.getElementById(key);
      if (el) {
        const headerHeight = 64;
        const top =
          el.getBoundingClientRect().top + window.pageYOffset - headerHeight;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } else {
      navigate(`/products#${key}`);
    }
    setDrawerOpen(false);
  };

  // 产品服务的下拉菜单配置
  const productsDropdown = {
    items: productSubItems,
    onClick: handleSubItemClick,
  };

  return (
    <Layout className="app-layout">
      <Header className="app-header">
        <div className="header-inner">
          {/* Logo */}
          <div className="logo" onClick={() => navigate('/')}>
            <img src={logo} alt="logo" className="logo-img" />
          </div>

          {/* 桌面端：中间导航 */}
          {!isMobile && (
            <div className="nav-center">
              {menuItems.map((item) => {
                if (item.key === '/products') {
                  // 产品服务 - 悬浮下拉
                  return (
                    <Dropdown
                      key={item.key}
                      menu={productsDropdown}
                      trigger={['hover']}
                      placement="bottom"
                      overlayClassName="products-dropdown-overlay"
                      // 切换成自定义 motionName 且 CSS 中不定义对应 keyframe,
                      // 相当于显式禁用 antd 默认的 slide-up 动画 ——
                      // 否则 React.StrictMode 双挂载会让默认动画播两次,出现下拉闪两次的 bug。
                      motion={{ motionName: 'products-dropdown-noop' }}
                    >
                      <span
                        className={`nav-products-trigger ${
                          currentKey === '/products' ? 'nav-products-trigger--active' : ''
                        }`}
                        onClick={() => navigate('/products')}
                      >
                        {item.label}
                      </span>
                    </Dropdown>
                  );
                }
                // 其他菜单项 — 用自定义 span,避免 antd Menu 在窄屏下自动折叠成「...」
                return (
                  <span
                    key={item.key}
                    className={`nav-item ${
                      currentKey === item.key ? 'nav-item--active' : ''
                    }`}
                    onClick={() => navigate(item.key)}
                  >
                    {item.label}
                  </span>
                );
              })}
            </div>
          )}

          {/* 桌面端：右侧操作 */}
          {!isMobile && (
            <div className="header-actions">
              <Button
                type="link"
                className="btn-login"
                onClick={() => {
                  openErpInSameTab('https://erp.chix.vip/login?redirect=%2Findex');
                }}
              >
                登录
              </Button>
              <Button
                type="primary"
                className="btn-trial"

                onClick={() => {
                  openErpInSameTab(
                    'https://demo.chix.vip/login?redirect=%2Findex&tenantCode=default'
                  );
                }}
              >
                体验演示
              </Button>
         
            </div>
          )}

          {/* 移动端汉堡按钮 */}
          {isMobile && (
            <Button
              type="text"
              icon={
                <MenuOutlined style={{ fontSize: '1.25rem', color: '#333' }} />
              }
              onClick={() => setDrawerOpen(true)}
              className="mobile-menu-btn"
            />
          )}
        </div>
      </Header>

      {/* 移动端抽屉导航 */}
      <Drawer
        title="导航菜单"
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={260}
        className="mobile-drawer"
      >
        <Menu
          mode="vertical"
          selectedKeys={[currentKey]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ border: 'none' }}
        />
        <div className="drawer-actions">
          <Button
            type="primary"
            block
            style={{ marginBottom: '0.75rem' }}
            onClick={() => {
              navigate('/contact');
              setDrawerOpen(false);
            }}
          >
            申请试用
          </Button>
          <Button
            block
            onClick={() => {
              openErpInSameTab(
                'https://demo.chix.vip/login?redirect=%2Findex&tenantCode=default'
              );
            }}
          >
            登录
          </Button>
          <div className="drawer-phone">
            <PhoneOutlined /> 18620706846
          </div>
        </div>
      </Drawer>

      <Content className="app-content">
        <Outlet />
      </Content>

      <Footer className="app-footer">
        <div className="footer-main">
          <div className="footer-inner">
            {/* 左侧品牌 */}
            <div className="footer-brand">
              <div className="footer-logo">
                <img src={logo_white} alt="logo" className="footer-logo-img" />
              </div>

            </div>

            {/* 产品服务 */}
            <div className="footer-col">
              <h4>产品服务</h4>
              <ul>
                <li>
                  <a href="/products">平台 · 云会计</a>
                </li>
                <li>
                  <a href="/products">平台 · 进销存</a>
                </li>
                <li>
                  <a href="/products">业财一体化</a>
                </li>
                <li>
                  <a href="/products">服务与支持</a>
                </li>
              </ul>
            </div>

            {/* 快速入口 — 完全镜像顶部 tab 的 6 项,点击走 SPA 路由 */}
            <div className="footer-col">
              <h4>快速入口</h4>
              <ul>
                {menuItems.map((item) => (
                  <li key={item.key}>
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(item.key);
                      }}
                      href={item.key}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* 联系方式 */}
            <div className="footer-col footer-contact">
              <h4>联系方式</h4>
              <ul>
                <li>
                  <EnvironmentOutlined />
                  <span>广州市天河区棠东东路7号115房</span>
                </li>
              </ul>
            </div>

            {/* 关注与服务 */}
            {/* <div className="footer-col footer-qrcodes">
              <h4>关注与服务</h4>
              <div className="qrcode-list">
                <div className="qrcode-item">
                  <div
                    className="qrcode-placeholder"
                    style={{ backgroundImage: `url(${qrFrame})` }}
                  >
                    <div className="qrcode-inner">
                      <img src={qrPlaceholder} alt="公众号" loading="lazy" decoding="async" />
                    </div>
                  </div>
                  <span>公众号</span>
                </div>
                <div className="qrcode-item">
                  <div
                    className="qrcode-placeholder"
                    style={{ backgroundImage: `url(${qrFrame})` }}
                  >
                    <div className="qrcode-inner">
                      <img src={qrPlaceholder} alt="客户顾问" loading="lazy" decoding="async" />
                    </div>
                  </div>
                  <span>客户顾问</span>
                </div>
              </div>
            </div> */}
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-bottom-inner">
            <span>&copy;2026-2036 广州赤兴科技有限公司</span>
            <span>粤ICP备2026075215号</span>
          </div>
        </div>
      </Footer>

      {/* 全站悬浮在线客服 —— 微信咨询 / 免费试用 */}
      <ChatWidget />
    </Layout>
  );
};

export default AppLayout;
